import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { X, ArrowRight, Upload, PiggyBank, Percent, CheckCircle } from 'lucide-react'
import { TopSection } from '../../ui/TopSection'
import { StatCard } from '../../ui/StatCard'
import { metricIcon } from '../../ui/metricIcons'
import { useTimeControls } from '../../../hooks/useTimeControls'
import { buildTieredOptions, countTieredOptions, TieredOptions } from '../../../services/sourcing/sourcingHelper'
import { Product } from '../../../stores/appReportsStore'
import { ProductAggregation } from '../../orderhistory/types'
import { Platform } from '../../../types/Platforms'
import { cn } from '../../../lib/utils'
import { SupplierLogo } from '../../ui/SupplierLogo'
import { aggregationToProduct, unitPriceSavingsFraction, formatUnitPrice } from './supplierCompareShared'
import { SupplierProduct } from '../../../services/sourcing/supplierComparisonService'
import { useAppSettingsStore } from '../../../stores/appSettingsStore'
import {
  whiteBG,
  primaryText,
  smallTextSize,
  mediumText,
  semiBoldText,
  boldText,
  tertiaryText,
  secondaryText,
  borderPrimary,
  extraSmallTextSize,
  baseTextSize,
  largeTextSize,
  textBlue,
  lightBlueBG,
  roundedLG,
  iconBgGreen,
  iconBgPurple,
} from '../../../styling/stylingHelper'

interface OrderHistorySupplierCompareViewProps {
  products: ProductAggregation[]
  supplierName: string
  optionsByArticleId: Record<string, Product[]>
  supplierProducts: SupplierProduct[]
  onGoBack: () => void
  onNewFile: () => void
}

/** Formats a supplier pack size into a kg / l / piece label. */
function formatPackSize(amount: number, stdUnit: string, pieceLabel: string): string {
  const u = (stdUnit || '').toLowerCase()
  if (u === 'g' || u === 'gram') return `${(amount / 1000).toFixed(2)} kg`
  if (u === 'ml' || u === 'milliliter') return `${(amount / 1000).toFixed(2)} l`
  return `${amount} ${pieceLabel}`
}

// Quality bar for surfacing a cheaper supplier match in the main list. Lower than sourcing's
// auto-select bar (75) because cross-supplier swaps (e.g. cod ↔ pangasius) rarely score 90+,
// but we still want a genuine match. The match % is shown so the user can judge.
const COMPARE_MIN_MATCH = 60

/** Best cheaper supplier match for a product = highest matchScore among the cheaper tier. */
function pickBestCheaper(tiers: TieredOptions): Product | undefined {
  if (tiers.cheaper.length === 0) return undefined
  return tiers.cheaper.reduce((best, cur) => (cur.matchScore > best.matchScore ? cur : best))
}

function formatOrderCount(agg: ProductAggregation): string {
  const raw = agg.aggregatedQuantityInfo?.totalRawQuantity ?? agg.qty ?? 0
  if (!(raw > 0)) return '--'
  return raw.toFixed(2)
}

function QuantityBadge({ text }: { text: string }) {
  return (
    <span className={cn(textBlue, smallTextSize, semiBoldText, lightBlueBG, roundedLG, 'px-2 py-0.5')}>
      {text}
    </span>
  )
}

function ProductOrderMeta({ agg }: { agg: ProductAggregation }) {
  const { t } = useTranslation()
  const orderCount = formatOrderCount(agg)
  const quantityLabel = agg.formattedQuantity
  return (
    <div className={cn('flex items-center gap-2 flex-wrap mt-0.5')}>
      <span className={cn(tertiaryText, extraSmallTextSize)}>
        {orderCount}x {t('products.ordered')}
      </span>
      {quantityLabel && <QuantityBadge text={quantityLabel} />}
    </div>
  )
}

function PlatformLogo({ platform, size = 'medium' }: { platform: string; size?: 'small' | 'medium' }) {
  return <SupplierLogo platform={Platform.fromString(platform)} size={size} alt={platform} />
}

export default function OrderHistorySupplierCompareView({
  products,
  supplierName,
  optionsByArticleId,
  supplierProducts,
  onGoBack,
  onNewFile,
}: OrderHistorySupplierCompareViewProps) {
  const { t } = useTranslation()
  const darkMode = useAppSettingsStore(s => s.darkMode)

  const {
    dateRange,
    setDateRange,
    selectedTimeOption,
    setSelectedTimeOption,
  } = useTimeControls()

  const productsMap = useMemo(() => {
    const map: Record<string, Product> = {}
    for (const agg of products) map[agg.articleId] = aggregationToProduct(agg)
    return map
  }, [products])

  const aggregationByArticleId = useMemo(() => {
    const map: Record<string, ProductAggregation> = {}
    for (const agg of products) map[agg.articleId] = agg
    return map
  }, [products])

  const periodSpend = useMemo(() => {
    const m: Record<string, number> = {}
    for (const agg of products) m[agg.articleId] = agg.spend || 0
    return m
  }, [products])

  // Build tiers + initial best-cheaper selection from the comparison result.
  const tieredMap = useMemo(() => {
    const tiers: Record<string, TieredOptions> = {}
    for (const [articleId, parent] of Object.entries(productsMap)) {
      tiers[articleId] = buildTieredOptions(parent, optionsByArticleId[articleId] || [])
    }
    return tiers
  }, [productsMap, optionsByArticleId])

  const [selectedOptionMap, setSelectedOptionMap] = useState<Record<string, Product>>(() => {
    const selections: Record<string, Product> = {}
    for (const articleId of Object.keys(productsMap)) {
      const best = pickBestCheaper(tieredMap[articleId])
      if (best) selections[articleId] = best
    }
    return selections
  })

  const [detailArticleId, setDetailArticleId] = useState<string | null>(null)
  const [isPanelVisible, setIsPanelVisible] = useState(false)

  // Only surface products that actually have a cheaper supplier match.
  const sortedEntries = useMemo(() => {
    return Object.entries(productsMap)
      .map(([articleId, parent]) => {
        const selected = selectedOptionMap[articleId]
        const isSameProduct = !selected || selected.articleId === articleId
        const currentSpend = periodSpend[articleId] || 0
        const frac = Math.max(0, unitPriceSavingsFraction(parent, selected))
        const savings = currentSpend * frac
        const projectedSpend = currentSpend - savings
        return { articleId, parent, selected, currentSpend, projectedSpend, savings, savingsPercent: frac * 100, isSameProduct }
      })
      // Surface a product only when it has a cheaper, good-enough supplier match.
      .filter(e => !e.isSameProduct && e.savings > 0 && (e.selected?.matchScore ?? 0) >= COMPARE_MIN_MATCH)
      .sort((a, b) => b.savings - a.savings)
  }, [productsMap, selectedOptionMap, periodSpend])

  const totalCurrentSpend = useMemo(() =>
    sortedEntries.reduce((sum, e) => sum + e.currentSpend, 0), [sortedEntries])
  const totalSavings = useMemo(() =>
    sortedEntries.reduce((sum, e) => sum + Math.max(0, e.savings), 0), [sortedEntries])
  const totalSavingsPercent = useMemo(() =>
    totalCurrentSpend > 0 ? (totalSavings / totalCurrentSpend) * 100 : 0, [totalCurrentSpend, totalSavings])

  const openDetail = (articleId: string) => {
    setDetailArticleId(articleId)
    requestAnimationFrame(() => setIsPanelVisible(true))
  }
  const closeDetail = () => {
    setIsPanelVisible(false)
    setTimeout(() => setDetailArticleId(null), 250)
  }
  const handleSelectOption = (articleId: string, option: Product) => {
    setSelectedOptionMap(prev => ({ ...prev, [articleId]: option }))
  }

  const detailParent = detailArticleId ? productsMap[detailArticleId] : null
  const detailTiers = detailArticleId ? tieredMap[detailArticleId] : undefined
  const detailCurrentSpend = detailArticleId ? (periodSpend[detailArticleId] || 0) : 0

  const renderOption = (option: Product, idx: number, showSavings: boolean) => {
    if (!detailParent || !detailArticleId) return null
    const isSelected = selectedOptionMap[detailArticleId]?.articleId === option.articleId
    const optionFraction = unitPriceSavingsFraction(detailParent, option)
    const optionSavings = detailCurrentSpend * optionFraction
    const optionSavingsPercent = optionFraction * 100
    const projectedSpend = detailCurrentSpend - optionSavings

    return (
      <div
        key={`${option.articleId}-${idx}`}
        className={cn(
          'rounded-xl border p-4 cursor-pointer transition-all',
          borderPrimary,
          isSelected
            ? 'border-green-400 bg-green-50/50 dark:bg-green-950/20 ring-1 ring-green-400 dark:ring-green-600'
            : 'hover:border-gray-300 dark:hover:border-gray-600'
        )}
        onClick={() => handleSelectOption(detailArticleId, option)}
      >
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-9 h-9 rounded-lg bg-gray-50 dark:bg-gray-800/50 flex items-center justify-center">
            <PlatformLogo platform={option.platform.toString()} size="small" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className={cn(primaryText, smallTextSize, semiBoldText, 'truncate')}>
                {option.name}
              </span>
              {isSelected && (
                <span className="shrink-0 text-xs px-1.5 py-0.5 rounded-full bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300">
                  {t('products.selectedLabel')}
                </span>
              )}
            </div>
            {(option.restaurantName || option.brand) && (
              <div className={cn(tertiaryText, extraSmallTextSize, 'mt-0.5')}>
                {[option.restaurantName, option.brand].filter(Boolean).join(' · ')}
              </div>
            )}
          </div>
          {showSavings && optionSavings !== 0 && (
            <div className={cn(
              'shrink-0 text-center px-3 py-1.5 rounded-lg',
              optionSavings > 0 ? 'bg-green-50 dark:bg-green-950/30' : 'bg-red-50 dark:bg-red-950/30'
            )}>
              <div className={cn(smallTextSize, boldText)} style={{ color: optionSavings > 0 ? (darkMode ? '#34D399' : '#16a34a') : (darkMode ? '#FCA5A5' : '#ef4444') }}>
                {optionSavings > 0 ? '-' : '+'}{Math.abs(optionSavingsPercent).toFixed(0)}%
              </div>
              <div className={extraSmallTextSize} style={{ color: optionSavings > 0 ? (darkMode ? '#34D399' : '#16a34a') : (darkMode ? '#FCA5A5' : '#ef4444') }}>
                €{Math.abs(optionSavings).toFixed(2)}
              </div>
            </div>
          )}
        </div>
        <div className="flex items-baseline gap-4 mt-3 pl-12">
          <span className={cn(primaryText, baseTextSize, boldText)}>
            {formatUnitPrice(option)}
          </span>
          {showSavings && detailCurrentSpend > 0 && (
            <span className={cn(tertiaryText, extraSmallTextSize)}>
              €{projectedSpend.toFixed(2)} {t('products.inPeriod')}
            </span>
          )}
        </div>
      </div>
    )
  }

  const tierSection = (title: string, hint: string | null, options: Product[], showSavings: boolean) => {
    if (options.length === 0) return null
    return (
      <div className="flex flex-col gap-2.5">
        <div className="px-1 pt-1">
          <div className={cn(tertiaryText, extraSmallTextSize, 'uppercase tracking-wide font-medium')}>
            {title} ({options.length})
          </div>
          {hint && <div className={cn(tertiaryText, extraSmallTextSize, 'mt-0.5')}>{hint}</div>}
        </div>
        {options.map((option, idx) => renderOption(option, idx, showSavings))}
      </div>
    )
  }

  return (
    <div>
      <TopSection
        title={t('products.compareSupplier.title')}
        showMobileTitle
        backButton={true}
        handleGoBack={onGoBack}
        dateRange={dateRange}
        setDateRange={setDateRange}
        selectedTimeOption={selectedTimeOption}
        setSelectedTimeOption={setSelectedTimeOption}
      />

      <div className="flex flex-col gap-6">
        {/* Compared-against header */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <span className={cn(secondaryText, smallTextSize)}>
            {supplierName
              ? t('products.compareSupplier.comparingAgainst', { supplier: supplierName })
              : t('products.compareSupplier.comparingAgainstFile')}
          </span>
          <button onClick={onNewFile} className={cn(whiteBG, borderPrimary, 'flex items-center gap-2 rounded-lg border px-3 py-2', primaryText, smallTextSize, semiBoldText)}>
            <Upload className="w-4 h-4" />
            {t('products.compareSupplier.newFile')}
          </button>
        </div>

        {/* KPI Cards — 2-col compact grid on phones */}
        <div className="grid grid-cols-2 gap-3 sm:flex sm:flex-col sm:gap-6 min-[1250px]:flex-row">
          <div className="w-full min-[1250px]:flex-1">
            <StatCard
              {...metricIcon('spend')}
              title={t('orderHistory.totalSpent')}
              value={totalCurrentSpend}
              valueType="currency"
              mobileLayout="tile"
            />
          </div>
          <div className="w-full min-[1250px]:flex-1">
            <StatCard
              icon={<PiggyBank className="w-6 h-6" style={{ color: '#079455' }} />}
              iconBg={iconBgGreen}
              title={t('products.totalSavings')}
              value={totalSavings}
              valueType="currency"
              mobileLayout="tile"
            />
          </div>
          <div className="w-full min-[1250px]:flex-1">
            <StatCard
              icon={<Percent className="w-6 h-6" style={{ color: '#079455' }} />}
              iconBg={iconBgGreen}
              title={t('products.savingsPercent')}
              value={totalSavingsPercent}
              valueType="percentage"
              mobileLayout="tile"
            />
          </div>
          <div className="w-full min-[1250px]:flex-1">
            <StatCard
              icon={<CheckCircle className="w-6 h-6" style={{ color: '#6938EF' }} />}
              iconBg={iconBgPurple}
              title={t('products.compareSupplier.matchesFound')}
              value={sortedEntries.length}
              valueType="number"
              mobileLayout="tile"
            />
          </div>
        </div>

        {/* Empty state */}
        {sortedEntries.length === 0 && (
          <div className={cn(whiteBG, 'rounded-xl flex items-center justify-center py-16', tertiaryText, smallTextSize)}>
            {t('products.compareSupplier.noMatches')}
          </div>
        )}

        {/* Product comparison cards */}
        {sortedEntries.length > 0 && (
          <div className="flex flex-col gap-4">
            {sortedEntries.map(({ articleId, parent, selected, currentSpend, projectedSpend, savings, savingsPercent }) => {
              const displayOption = selected!
              const optionCount = tieredMap[articleId] ? countTieredOptions(tieredMap[articleId]) : 0
              const parentAgg = aggregationByArticleId[articleId]

              return (
                <div
                  key={articleId}
                  className={cn(whiteBG, 'rounded-xl cursor-pointer transition-all hover:shadow-md', 'border border-transparent hover:border-gray-200 dark:hover:border-gray-700')}
                  onClick={() => openDetail(articleId)}
                >
                  {/* Stacks vertically on phones; desktop row re-asserted at sm. */}
                  <div className="flex flex-col sm:flex-row items-stretch">
                    {/* LEFT: current product */}
                    <div className="flex-1 p-4 min-[900px]:p-5 min-w-0">
                      <div className="flex items-start gap-3">
                        <div className="shrink-0 w-10 h-10 rounded-lg bg-gray-50 dark:bg-gray-800/50 flex items-center justify-center">
                          <PlatformLogo platform={parent.platform.toString()} size="medium" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className={cn(primaryText, smallTextSize, semiBoldText, 'truncate')}>{parent.name}</div>
                          {parentAgg && <ProductOrderMeta agg={parentAgg} />}
                        </div>
                      </div>
                      <div className="mt-3 flex items-baseline gap-3">
                        <span className={cn(primaryText, largeTextSize, boldText)}>{formatUnitPrice(parent)}</span>
                        <span className={cn(tertiaryText, extraSmallTextSize)}>
                          €{currentSpend.toFixed(2)} {t('products.inPeriod')}
                        </span>
                      </div>
                    </div>

                    {/* ARROW */}
                    <div className="hidden sm:flex items-center px-2 min-[900px]:px-4">
                      <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                        <ArrowRight className={cn(tertiaryText, 'w-4 h-4')} />
                      </div>
                    </div>

                    {/* RIGHT: supplier match */}
                    <div className="flex-1 p-4 min-[900px]:p-5 min-w-0">
                      <div className="flex items-start gap-3">
                        <div className="shrink-0 w-10 h-10 rounded-lg bg-gray-50 dark:bg-gray-800/50 flex items-center justify-center">
                          <PlatformLogo platform={displayOption.platform.toString()} size="medium" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className={cn(primaryText, smallTextSize, semiBoldText, 'truncate')}>{displayOption.name}</span>
                            <span className={cn('shrink-0 text-xs px-1.5 py-0.5 rounded-full', 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300')}>
                              {t('products.compareSupplier.matchPct', { score: Math.round(displayOption.matchScore) })}
                            </span>
                            {optionCount > 1 && (
                              <span className={cn('shrink-0 text-xs px-1.5 py-0.5 rounded-full', 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400')}>
                                +{optionCount - 1}
                              </span>
                            )}
                          </div>
                          <div className={cn(tertiaryText, extraSmallTextSize, 'mt-0.5')}>
                            {displayOption.restaurantName || supplierName || ' '}
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 flex items-baseline gap-3">
                        <span className={cn(primaryText, largeTextSize, boldText)}>{formatUnitPrice(displayOption)}</span>
                        <span className={cn(tertiaryText, extraSmallTextSize)}>
                          €{projectedSpend.toFixed(2)} {t('products.inPeriod')}
                        </span>
                      </div>
                    </div>

                    {/* SAVINGS */}
                    <div className={cn('shrink-0 flex flex-col items-center justify-center px-4 min-[900px]:px-6 py-3 sm:py-0 border-t sm:border-t-0 sm:border-l', borderPrimary, 'min-w-[100px] min-[900px]:min-w-[120px]')}>
                      <div className={cn(baseTextSize, boldText)} style={{ color: darkMode ? '#34D399' : '#16a34a' }}>
                        -{savingsPercent.toFixed(0)}%
                      </div>
                      <div className={cn(smallTextSize, semiBoldText, 'mt-0.5')} style={{ color: darkMode ? '#34D399' : '#16a34a' }}>
                        €{savings.toFixed(2)}
                      </div>
                      <div className={cn(tertiaryText, extraSmallTextSize, 'mt-0.5')}>
                        {t('products.savings').toLowerCase()}
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Full list of products read from the supplier file */}
        {supplierProducts.length > 0 && (
          <div className={cn(whiteBG, 'rounded-xl border overflow-hidden', borderPrimary)}>
            <div className={cn('px-4 py-3 border-b', borderPrimary)}>
              <span className={cn(primaryText, smallTextSize, semiBoldText)}>
                {t('products.compareSupplier.fileProductsTitle', { count: supplierProducts.length })}
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className={cn(tertiaryText, extraSmallTextSize, 'uppercase tracking-wide text-left border-b', borderPrimary)}>
                    <th className="px-4 py-2 font-medium">{t('products.compareSupplier.colProduct')}</th>
                    <th className="px-4 py-2 font-medium w-32">{t('products.compareSupplier.colQuantity')}</th>
                    <th className="px-4 py-2 font-medium w-36">{t('products.compareSupplier.colUnitPrice')}</th>
                  </tr>
                </thead>
                <tbody>
                  {supplierProducts.map((sp, i) => (
                    <tr key={`${sp.articleId}-${i}`} className={cn('border-b last:border-b-0', borderPrimary)}>
                      <td className={cn('px-4 py-2', primaryText, smallTextSize)}>{sp.name}</td>
                      <td className={cn('px-4 py-2', secondaryText, smallTextSize)}>
                        {sp.packLabel || formatPackSize(sp.standardizedAmount, sp.stdUnit, t('products.compareSupplier.pieces'))}
                      </td>
                      <td className={cn('px-4 py-2', primaryText, smallTextSize, semiBoldText)}>
                        {sp.unitPrice > 0 ? `€${sp.unitPrice.toFixed(2)}/${sp.unitPriceUnit}` : '--'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Side panel */}
      {detailArticleId && (
        <div className="fixed inset-0 z-30 flex justify-end bg-[#111827]/40 dark:bg-[#111827]/40 pt-[72px] md:pt-0" onClick={closeDetail}>
          <div
            className={cn('h-full w-full max-w-full md:max-w-[560px] bg-white dark:bg-background-dark shadow-xl flex flex-col transform transition-all duration-250 ease-out', isPanelVisible ? 'translate-x-0' : 'translate-x-full')}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={cn('px-5 py-5 border-b', borderPrimary)}>
              <div className="flex items-center justify-between mb-4">
                <span className={cn(tertiaryText, extraSmallTextSize, 'uppercase tracking-wide font-medium')}>
                  {t('products.alternatives')}
                </span>
                <button onClick={closeDetail} className={cn(tertiaryText, 'p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800')}>
                  <X className="w-5 h-5" />
                </button>
              </div>
              {detailParent && detailArticleId && (
                <div className="flex items-start gap-3">
                  <div className="shrink-0 w-10 h-10 rounded-lg bg-gray-50 dark:bg-gray-800/50 flex items-center justify-center">
                    <PlatformLogo platform={detailParent.platform.toString()} size="medium" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className={cn(primaryText, mediumText, semiBoldText, 'truncate')}>{detailParent.name}</div>
                    {aggregationByArticleId[detailArticleId] && (
                      <ProductOrderMeta agg={aggregationByArticleId[detailArticleId]} />
                    )}
                    <div className="flex items-baseline gap-3 mt-1">
                      <span className={cn(primaryText, baseTextSize, boldText)}>{formatUnitPrice(detailParent)}</span>
                      <span className={cn(tertiaryText, extraSmallTextSize)}>
                        €{detailCurrentSpend.toFixed(2)} {t('products.inPeriod')}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-5">
              {detailTiers && tierSection(t('products.tierCheaper'), null, detailTiers.cheaper, true)}
              {detailTiers && tierSection(t('products.tierUncomparable'), t('products.tierUncomparableHint'), detailTiers.uncomparable, false)}
              {detailTiers && tierSection(t('products.tierMoreExpensive'), null, detailTiers.expensive, true)}

              {(!detailTiers || countTieredOptions(detailTiers) === 0) && (
                <div className={cn('flex items-center justify-center py-8', tertiaryText, smallTextSize)}>
                  {t('products.noAlternatives')}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
